import React, { useEffect } from 'react'
import { addDays, subDays } from 'date-fns'
import { Container, Contents, LoadingContainer, AlertMessage } from './styles'
import { SessionTitle } from '../SessionTitle'
import { IconComponent } from '~/assets/icons/component'
import { SpinnerIconLoader } from '~/components/shared'
import { useLaboratoryHealthProcedures, usePatient } from '~/context'
import { LaboratoryTestQualitativeProperty, LaboratoryTestQuantitativeProperty } from '~/interfaces'
import { message } from '~/services'
import { getQuantitativePropertyReferenceValue } from '~/utils'

const UrinalysisContent: React.FC = () => {
  const { patient } = usePatient()
  const { selectedUrineCulture, fetchUrinalysisTest, urinalysis, urinalysisLoading, urinalysisError } =
    useLaboratoryHealthProcedures()

  useEffect(() => {
    if (selectedUrineCulture) {
      const { startDate } = selectedUrineCulture
      const TEST_CORRELATION_LOWER_BOUND = subDays(new Date(startDate), 15).toISOString()
      const TEST_CORRELATION_UPPER_BOUND = addDays(new Date(startDate), 5).toISOString()
      const URINALYSIS_TEST_CODES = [21648947]

      fetchUrinalysisTest({
        pathParams: { patientId: patient.id },
        queryParams: {
          testCodes: URINALYSIS_TEST_CODES,
          startDate: TEST_CORRELATION_LOWER_BOUND,
          endDate: TEST_CORRELATION_UPPER_BOUND
        }
      })
    }
  }, [fetchUrinalysisTest, patient.id, selectedUrineCulture])

  const renderLoading = () => {
    return (
      <LoadingContainer>
        <SpinnerIconLoader iconComponent={<IconComponent name={'loading'} />} size={75} velocity={2} />
      </LoadingContainer>
    )
  }

  const renderMessage = () => {
    if (urinalysis && urinalysis?.length === 0) {
      return (
        <AlertMessage>
          <span>{message('laboratory.not_found_urinalysis')}</span>
        </AlertMessage>
      )
    }

    return (
      <AlertMessage>
        <span>{urinalysisError?.message}</span>
      </AlertMessage>
    )
  }

  const renderContent = (properties: (LaboratoryTestQuantitativeProperty | LaboratoryTestQualitativeProperty)[]) => {
    const getQualitativeRef = (property: LaboratoryTestQualitativeProperty) => {
      return property.referenceValue
    }
    const getQuantitativeRef = (property: LaboratoryTestQuantitativeProperty) => {
      return getQuantitativePropertyReferenceValue({
        lowerValue: property?.lowerReferenceValue,
        upperValue: property?.upperReferenceValue
      })
    }

    return (
      <table>
        <tr className="header">
          <th>{message('laboratory.item')}</th>
          <th>{message('laboratory.result')}</th>
          <th>{message('laboratory.reference')}</th>
        </tr>
        {properties.map((property, index) => (
          <tr data-testid="table rows" key={index}>
            <td>{property.name}</td>
            <td>{property.value}</td>
            {'referenceValue' in property && <td>{getQualitativeRef(property)}</td>}
            {'lowerReferenceValue' in property && <td>{getQuantitativeRef(property)}</td>}
          </tr>
        ))}
      </table>
    )
  }

  const render = () => {
    if (urinalysisError) return renderMessage()
    if (urinalysisLoading) return renderLoading()
    if (urinalysis) return renderContent(urinalysis as LaboratoryTestQuantitativeProperty[])
  }

  return (
    <Container>
      <SessionTitle title={message('laboratory.urinalysis')} descriptionDate={selectedUrineCulture?.startDate} />
      <Contents>{render()}</Contents>
    </Container>
  )
}

export { UrinalysisContent }
